import { usePermissions as useMaterialPermissions } from '../../../hooks/usePermissions';
import MaterialTransitionFields from '../../components/MaterialTransitionFields';
import { TRANSITION_TEXTS } from '../../constants/materialValuationTexts';
import { materialTransitionPayload } from '../../utils/materialTransitionPayload';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ConfirmationDialog,
  CustomTextArea,
  CustomTextField,
  DecimalTextField,
  DialogActions,
  DialogBody,
  DialogContainer,
  DialogTitle,
  GeneralTableBox,
  HeadTableCell,
  NoDataSkeleton,
  RowTableCell,
} from '../../../common';
import { TABLE_INPUT_CELL_SX, TABLE_INPUT_SX } from '../../../common/styles';
import {
  buildReceiveSupplyOrderPayload,
  formatSupplyOrderQuantity,
  getSupplyOrderApiErrorMessage,
} from '../utils/supplyOrdersUtils';
import { guardDialogClose } from '../../utils/actionLockUtils';
import {
  RECEIVE_SUPPLY_ORDER_FORM_TEXTS,
  RECEIVE_VALUATION_TEXTS,
  SUPPLY_ORDER_ITEM_TABLE_TEXTS as ITEM_TEXTS,
} from '../constants/supplyOrdersConstants';
import { materialMoney } from '../../../utils/materialDisplay';
import ReceiptAllocationFields from './ReceiptAllocationFields';

import ClientPaginatedTable from '../../../common/components/table/ClientPaginatedTable';
const MIN_TABLE_WIDTH = 760;
const textSx = { textAlign: 'left' };
const numberSx = { textAlign: 'right' };

const COLUMNS = [
  { label: ITEM_TEXTS.article, sx: textSx },
  { label: ITEM_TEXTS.requested, sx: { ...numberSx, width: 120 } },
  { label: ITEM_TEXTS.received, sx: { ...numberSx, width: 120 } },
  { label: ITEM_TEXTS.pending, sx: { ...numberSx, width: 120 } },
  { label: ITEM_TEXTS.receiveNow, sx: { ...numberSx, width: 162 } },
];

const ReceiveSupplyOrderDialog = ({
  open,
  onClose,
  order,
  unvaluedStock = [],
  onReceive,
  isProcessing = false,
}) => {
  const { permissions: materialPermissions } = useMaterialPermissions();
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [valuation, setValuation] = useState({});
  const [formState, setFormState] = useState({
    received_at: '',
    supplier_document_type: '',
    supplier_document_number: '',
    carrier_name: '',
    notes: '',
  });
  const [itemQuantities, setItemQuantities] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [valuationPayload, setValuationPayload] = useState(null);

  const receivableItems = useMemo(
    () =>
      (order?.items || []).filter(
        (item) =>
          Number(item.pending_quantity) > 0 &&
          !['cancelled', 'closed_with_difference'].includes(item.status)
      ),
    [order?.items]
  );

  useEffect(() => {
    if (open) {
      const today = new Date().toISOString().split('T')[0];
      setFormState({
        received_at: today,
        supplier_document_type: '',
        supplier_document_number: '',
        carrier_name: '',
        notes: '',
      });
      const initialQuantities = {};
      receivableItems.forEach((item) => {
        initialQuantities[item.uuid] = '';
      });
      setItemQuantities(initialQuantities);
      setRequestId(crypto.randomUUID());
      setValuation({});
      setValuationPayload(null);
      setErrorMessage('');
    }
  }, [open, order?.uuid, receivableItems]);

  const handleQuantityChange = (itemUuid, value) => {
    if (value === '') {
      setItemQuantities((prev) => ({ ...prev, [itemUuid]: '' }));
      if (errorMessage) setErrorMessage('');
      return;
    }

    setItemQuantities((prev) => ({ ...prev, [itemUuid]: value }));
    if (errorMessage) setErrorMessage('');
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleConfirm = async () => {
    const itemsWithQuantities = receivableItems.map((item) => ({
      ...item,
      receiveQuantity: itemQuantities[item.uuid],
    }));

    for (const item of itemsWithQuantities) {
      const quantity = Number(item.receiveQuantity);
      if (!Number.isFinite(quantity) || quantity <= 0) continue;
      const pending = Number(item.pending_quantity) || 0;
      if (quantity > pending) {
        setErrorMessage(
          `La cantidad a recibir no puede superar la pendiente (${formatSupplyOrderQuantity(pending)}) para ${item.article_name_snapshot}.`
        );
        return;
      }
    }

    const payload = buildReceiveSupplyOrderPayload(formState, itemsWithQuantities);
    if (!payload) {
      setErrorMessage('Ingresá al menos una cantidad a recibir mayor a cero.');
      return;
    }

    payload.request_id = requestId;
    payload.items = payload.items.map((item) => ({
      ...item,
      valuation: materialTransitionPayload(valuation[item.uuid_supply_order_item]),
    }));

    const receivedIds = new Set(
      payload.items
        .filter((item) => Number(item.received_quantity) > 0)
        .map((item) => item.uuid_supply_order_item)
    );
    // Con apertura previa no se puede valorizar desde la recepción: ese stock sigue sin valorar.
    const affected = unvaluedStock.filter(
      (item) => receivedIds.has(item.uuid_supply_order_item) && !item.has_opening
    );
    if (affected.length) {
      setValuationPayload({ ...payload, value_unvalued_stock: true });
      return;
    }
    await submitReceipt(payload);
  };

  const submitReceipt = async (payload) => {
    try {
      setErrorMessage('');
      await onReceive(payload);
      setValuationPayload(null);
      onClose();
    } catch (error) {
      setValuationPayload(null);
      setErrorMessage(getSupplyOrderApiErrorMessage(error, 'No se pudo registrar la recepción'));
    }
  };

  if (!open) return null;

  return (
    <DialogContainer
      open={open}
      onClose={guardDialogClose(isProcessing, onClose)}
      size="large"
      minHeight="60vh"
    >
      <DialogTitle>Registrar recepción — {order?.order_code || 'Orden'}</DialogTitle>
      <DialogBody>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          {order?.source_type === 'warehouse' && (
            <Alert severity="warning">
              Esta orden descuenta stock del depósito origen. Si no hay stock suficiente, la
              operación será rechazada por el backend.
            </Alert>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <CustomTextField
              label="Fecha de recepción"
              name="received_at"
              type="date"
              value={formState.received_at}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={isProcessing}
            />
            <CustomTextField
              label="Tipo de documento"
              name="supplier_document_type"
              value={formState.supplier_document_type}
              onChange={handleInputChange}
              fullWidth
              disabled={isProcessing}
            />
            <CustomTextField
              label="Número de documento"
              name="supplier_document_number"
              value={formState.supplier_document_number}
              onChange={handleInputChange}
              fullWidth
              disabled={isProcessing}
            />
            <CustomTextField
              label="Transportista"
              name="carrier_name"
              value={formState.carrier_name}
              onChange={handleInputChange}
              fullWidth
              disabled={isProcessing}
            />
          </Box>

          <CustomTextArea
            label={RECEIVE_SUPPLY_ORDER_FORM_TEXTS.notes}
            name="notes"
            value={formState.notes}
            onChange={handleInputChange}
            minRows={2}
            disabled={isProcessing}
          />

          <Typography variant="subtitle1" fontWeight={700}>
            Cantidades a recibir
          </Typography>

          {order?.origins?.length > 1 && (
            <ReceiptAllocationFields
              key={`${open}:${order.uuid}`}
              items={receivableItems}
              disabled={isProcessing}
              onAllocation={(values) =>
                setItemQuantities((previous) => ({ ...previous, ...values }))
              }
            />
          )}

          <ClientPaginatedTable rows={receivableItems} resetKey={order?.uuid}>
            {(pageRows) => (
              <GeneralTableBox hasMaxHeight={false} fixedLayout minTableWidth={MIN_TABLE_WIDTH}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {COLUMNS.map((column) => (
                        <HeadTableCell key={column.label} sx={column.sx}>
                          {column.label}
                        </HeadTableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {receivableItems.length === 0 ? (
                      <NoDataSkeleton
                        colSpan={COLUMNS.length}
                        message={ITEM_TEXTS.emptyPending}
                        showSubtitle={false}
                      />
                    ) : (
                      pageRows.map((item) => (
                        <TableRow key={item.uuid} hover>
                          <RowTableCell sx={textSx} title={item.article_name_snapshot}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, lineHeight: 1.25 }}
                              noWrap
                            >
                              {item.article_name_snapshot}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                              {item.article_code_snapshot}
                            </Typography>
                            {item.origin && (
                              <Typography variant="caption" component="div" title={item.origin.label}>
                                {item.origin.order_number} · {item.origin.label}
                              </Typography>
                            )}
                          </RowTableCell>
                          <RowTableCell sx={numberSx}>
                            {formatSupplyOrderQuantity(item.requested_quantity)}
                          </RowTableCell>
                          <RowTableCell sx={numberSx}>
                            {formatSupplyOrderQuantity(item.received_quantity)}
                          </RowTableCell>
                          <RowTableCell sx={numberSx}>
                            {formatSupplyOrderQuantity(item.pending_quantity)}
                          </RowTableCell>
                          <RowTableCell sx={{ ...numberSx, ...TABLE_INPUT_CELL_SX }}>
                            <DecimalTextField
                              value={itemQuantities[item.uuid] ?? ''}
                              onChange={(event) =>
                                handleQuantityChange(item.uuid, event.target.value)
                              }
                              inputProps={{ style: { textAlign: 'right' } }}
                              size="small"
                              sx={TABLE_INPUT_SX}
                              disabled={isProcessing}
                            />
                          </RowTableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </GeneralTableBox>
            )}
          </ClientPaginatedTable>

          {materialPermissions.canManageMaterialValuation
            && receivableItems
              .filter((item) => item.requires_transition_valuation)
              .map((item) => (
                <MaterialTransitionFields
                  key={item.uuid}
                  title={TRANSITION_TEXTS.itemTitle(item.article_name_snapshot)}
                  value={valuation[item.uuid] || {}}
                  onChange={(value) => setValuation((prev) => ({ ...prev, [item.uuid]: value }))}
                  disabled={isProcessing}
                />
              ))}
        </Box>
      </DialogBody>
      <DialogActions
        handleClose={onClose}
        handleSave={handleConfirm}
        saveLabel="Confirmar recepción"
        saveButtonProps={{ isLoading: isProcessing }}
      />
      <ConfirmationDialog
        open={Boolean(valuationPayload)}
        onClose={() => setValuationPayload(null)}
        onConfirm={() => submitReceipt(valuationPayload)}
        title={RECEIVE_VALUATION_TEXTS.title}
        message={RECEIVE_VALUATION_TEXTS.message(order?.order_code || '')}
        details={(
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {unvaluedStock
              .filter((item) =>
                valuationPayload?.items?.some(
                  (row) =>
                    row.uuid_supply_order_item === item.uuid_supply_order_item &&
                    Number(row.received_quantity) > 0
                )
              )
              .map((item) => {
                const displayItem = {
                  ...item,
                  unvalued_quantity: formatSupplyOrderQuantity(item.unvalued_quantity),
                };
                return (
                  <li key={item.uuid_supply_order_item}>
                    {item.has_opening
                      ? RECEIVE_VALUATION_TEXTS.openedLine(displayItem)
                      : RECEIVE_VALUATION_TEXTS.line(displayItem, materialMoney(item.unit_price))}
                  </li>
                );
              })}
          </Box>
        )}
        confirmText={RECEIVE_VALUATION_TEXTS.confirm}
        confirmVariant="base"
        isLoading={isProcessing}
      />
    </DialogContainer>
  );
};

export default ReceiveSupplyOrderDialog;
